import { Box, Button, Typography } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

import { useCreateRuleRulesPostMutation } from "api";
import { SelectInput, SwitchInput, TextInput } from "components/forms";
import { IRuleDeviceOffline } from "interfaces/rules.interface";

import { RuleDeviceOffline as config } from "../common/RulePresetMinMaxValues";
import { configurationSchema } from "../common/schemas";

const conditionsSchema = z
  .object({
    time_since_last_seen_gte: z.coerce
      .number()
      .min(config.time_since_last_seen_gte.exclusiveMinimum)
      .max(config.time_since_last_seen_gte.exclusiveMaximum)
      .default(config.time_since_last_seen_gte.default),
  })
  .nullable()
  .default(null);

const ruleDeviceOfflineSchema = z.object({
  conditions_preset: z.enum(["default", ""]).nullable().default("default"),
  conditions: conditionsSchema,
  configuration: configurationSchema,
});

type RuleDeviceOfflineSchema = z.infer<typeof ruleDeviceOfflineSchema>;

type RuleDeviceOfflineFormProps = {
  entityId: string;
  presets: { id: string; name: string }[];
  defaultValues: IRuleDeviceOffline | null;
  disabled: boolean;
  onClose: () => void;
};

export function RuleDeviceOfflineForm({
  entityId,
  presets,
  defaultValues,
  disabled,
  onClose,
}: RuleDeviceOfflineFormProps) {
  const { t } = useTranslation();

  const [createRule] = useCreateRuleRulesPostMutation();

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: ruleDeviceOfflineSchema.safeParse(defaultValues ?? {}).data,
    resolver: zodResolver(ruleDeviceOfflineSchema),
  });

  const onSubmit = async (data: RuleDeviceOfflineSchema) => {
    await createRule({
      ruleCreateModels: {
        ...data,
        type: "device.offline",
        entity_id: entityId,
        description: null,
        enabled: true,
        conditions_preset: data.conditions_preset || null,
      },
    });

    onClose();
  };

  const presetId = watch("conditions_preset");

  return (
    <Box
      component={"form"}
      onSubmit={handleSubmit(onSubmit, console.error)}
      sx={{ display: "flex", flexDirection: "column", flexGrow: 1, gap: 2 }}
    >
      <SelectInput
        control={control}
        label={t("preset")}
        fieldName={"conditions_preset"}
        options={presets}
        placeholder={t("custom")}
        optionLabelField={"name"}
        disabled={disabled}
        onChange={(event) => {
          if (event.target.value.length === 0) {
            const value = conditionsSchema.parse({});

            setValue("conditions", value);
          } else {
            setValue("conditions", null);
          }
        }}
      />
      {!presetId && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            padding: 1,
            border: 1,
            borderRadius: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant={"bodyLargeMedium"}>
            {t("send_notification_message")}
          </Typography>
          <Box
            sx={{
              padding: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <TextInput
              control={control}
              disabled={disabled}
              fieldName={"conditions.time_since_last_seen_gte"}
              label={t("downtime_is_greater_than_or_equal_to")}
              type={"number"}
              unit={t("mins")}
              min={config.time_since_last_seen_gte.exclusiveMinimum}
              max={config.time_since_last_seen_gte.exclusiveMaximum}
            />
          </Box>
          <Typography variant={"bodyLargeMedium"}>
            {t("send_ok_message_only_through_SMS")}
          </Typography>
          <Box
            sx={{
              padding: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <SwitchInput
              control={control}
              disabled={disabled}
              fieldName={"configuration.execute_actions_on_alert_to_ok"}
              label={t("Enabled")}
            />
          </Box>
        </Box>
      )}
      <Box sx={{ flexGrow: 1 }} />
      {!disabled && <Button type={"submit"}>{t("Create")}</Button>}
    </Box>
  );
}
